import { Request, Response as ExpressResponse } from 'express';
import { GraphQLError } from 'graphql';
import { UNAUTHORIZED } from 'http-status-codes';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { createHttpLink } from 'apollo-link-http';
import { onError, ErrorLink, ErrorResponse } from 'apollo-link-error';
import { RestLink } from 'apollo-link-rest';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
  defaultDataIdFromObject
} from 'apollo-cache-inmemory';
import { always, tryCatch } from 'ramda';
import fetch from 'isomorphic-fetch';
import { History } from 'history';
import { RouteContext } from '@moda/portal-platform';
import introspectionQueryResultData from '../../generated/graphql';
import { SSRContext } from '../../types/SSRContext';
import { parseUser } from '../../lib/user';
import { getCookies, parseCookies } from '../../hooks/useCookies';
import { getUserFromCookies } from '../../hooks/useUser';
import { ERROR_MODAL_DEFAULTS } from '../../hooks/useErrorModal';
import { CHECKOUT_ERROR_DEFAULTS } from '../../hooks/useCheckout/useCheckoutError';
import { hrefFor } from '../../routers';
import { CONFIG } from '../../config';
import { isDevelopment } from '../../lib/isEnv';
import { ONE_YEAR_SECONDS } from '../../constants';
import { localTypeDefs } from './localTypeDefs';

const fragmentMatcher = new IntrospectionFragmentMatcher({ introspectionQueryResultData });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dataIdFromObject = (object: any) => {
  switch (object.__typename) {
    case 'Algorithm':
      // algorithms from user api don't have unique ids
      return `Algorithm:${object.attributes.algorithm_type}:${object.id}`;
    default:
      return defaultDataIdFromObject(object);
  }
};

type NetworkError = {
  response: Response;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;
  statusCode: number;
};

type GraphQLErrorWithCode = GraphQLError & { code: string };

const isUnauthorizedError = ({ networkError, graphQLErrors }: Partial<ErrorResponse>) =>
  (Array.isArray(graphQLErrors) &&
    graphQLErrors.some(error => (error as GraphQLErrorWithCode).code === 'unauthorized')) ||
  (networkError as NetworkError)?.statusCode === UNAUTHORIZED;

const createServerErrorHandler = ({
  routeContext,
  ssrContext
}: {
  routeContext: RouteContext;
  ssrContext: SSRContext;
}): ErrorLink.ErrorHandler => ({ graphQLErrors, networkError, operation, response }) => {
  if (isUnauthorizedError({ graphQLErrors, networkError })) {
    routeContext.url = hrefFor.LogoutPage();
    ssrContext.ignoreErrors = true;
  }

  // eslint-disable-next-line no-console
  console.error(
    JSON.stringify({
      msg: `Apollo: GraphQL Error during operation "${operation.operationName}"`,
      level: 50,
      graphQLErrors,
      networkError,
      operation,
      response
    })
  );
};

const createClientErrorHandler = (history: History): ErrorLink.ErrorHandler => ({
  graphQLErrors,
  networkError,
  operation,
  response
}) => {
  if (isUnauthorizedError({ graphQLErrors, networkError })) {
    if (response) response.errors = undefined;
    history.replace(hrefFor.LogoutPage());
  }

  // eslint-disable-next-line no-console
  console.error(`Apollo: GraphQL Error during operation "${operation.operationName}"\n%o`, {
    graphQLErrors,
    networkError,
    operation,
    response
  });
};

const commonConfiguration = {
  link: ApolloLink.from([
    new RestLink({
      customFetch: fetch,
      endpoints: {
        cart: CONFIG.MODA_API_URL,
        preferences: CONFIG.PREFERENCES_API_URL,
        user: CONFIG.USER_API_URL
      },
      uri: CONFIG.MODA_API_URL,
      headers: {
        'Content-Type': 'application/vnd.api+json'
      }
    }),
    createHttpLink({ uri: CONFIG.SEARCH_API_GRAPHQL_ENDPOINT, fetch })
  ]),
  typeDefs: localTypeDefs,
  resolvers: {}
};

export const createClientSideApolloClient = (history: History) => {
  const cache = new InMemoryCache({ fragmentMatcher, dataIdFromObject });
  const authLink = setContext((_, { headers }) => {
    const { accessToken } = getUserFromCookies();

    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : ''
      }
    };
  });

  cache.writeData({
    data: {
      user: getUserFromCookies(),
      cookies: getCookies(),
      errorModal: ERROR_MODAL_DEFAULTS,
      checkoutError: CHECKOUT_ERROR_DEFAULTS,
      notifications: []
    }
  });

  return new ApolloClient({
    ...commonConfiguration,
    link: onError(createClientErrorHandler(history))
      .concat(authLink)
      .concat(commonConfiguration.link),
    cache: cache.restore(window.__APOLLO_STATE__),
    connectToDevTools: isDevelopment()
      ? true
      : tryCatch(JSON.parse, always(false))(window.__ENV__?.DEV_TOOLS)
  });
};

export const createServerSideApolloClient = ({
  req,
  routeContext,
  ssrContext,
  res
}: {
  req: Request;
  routeContext: RouteContext;
  ssrContext: SSRContext;
  res: ExpressResponse;
}) => {
  const cache = new InMemoryCache({ fragmentMatcher, dataIdFromObject });

  const cookies = parseCookies({
    ...req.cookies,
    clientCountryCode: req.headers['CloudFront-Viewer-Country']
      ? req.headers['CloudFront-Viewer-Country'][0]
      : ''
  });

  req.headers['CloudFront-Viewer-Country'] &&
    res.cookie('clientCountryCode', req.headers['CloudFront-Viewer-Country'][0], {
      path: '/',
      maxAge: ONE_YEAR_SECONDS
    });

  const user = parseUser(cookies.fullUser);

  const authLink = setContext((_, { headers }) => {
    const { accessToken } = user;

    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : ''
      }
    };
  });

  cache.writeData({
    data: {
      user,
      cookies,
      errorModal: ERROR_MODAL_DEFAULTS,
      checkoutError: CHECKOUT_ERROR_DEFAULTS,
      notifications: []
    }
  });

  return new ApolloClient({
    ...commonConfiguration,
    link: onError(createServerErrorHandler({ routeContext, ssrContext }))
      .concat(authLink)
      .concat(commonConfiguration.link),
    ssrMode: true,
    cache: cache
  });
};
